import { useEffect, useState, useContext } from "react";
import Button from "@mui/material/Button";
import localize from "../../common/localize";
import { Alert, Box, Drawer, IconButton, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import { ModuleContext } from "./ModuleGuard";
import { isModuleActionRestricted } from "../../common/EntitiesManagement";

export default function ModalBackground({
  title,
  showModal,
  setShowModal,
  Component,
  onClickToSubmit,
  onClickToCancel,
  hideCancelButton,
  hideSubmitButton,
  okButtonText,
  cancelButtonText,
  validators,
  okButtonAlwaysAvailable,
  isDrawerMode,
  hasRequiredValues,
  hideFooterControls,
  customSecondaryAction, // {name, action}
  customHeight,
  isEditForm,
}) {
  const [okButtonDisabled, setOkButtonDisabled] = useState(false);
  const moduleForm = useContext(ModuleContext);
  // On edit modals, remove the Save button when the module's MODIFY is restricted.
  const submitHidden =
    hideSubmitButton ||
    (isEditForm &&
      moduleForm &&
      isModuleActionRestricted(moduleForm, "MODIFY"));

  useEffect(() => {
    if (!showModal) {
      setOkButtonDisabled(false);
    }
  }, [showModal]);

  const showAsModal = () => {
    return (
      <>
        <div
          className="modal_page_background justify-center items-center flex fixed inset-0 z-50 outline-none focus:outline-none"
          style={{ padding: "16px", zIndex: 1401 }}
        >
          <div className={`relative my-2`} style={{ maxWidth: "100%", display: "flex" }}>
            {/* content */}
            <div
              style={{ maxWidth: "100%", boxSizing: "border-box" }}
              className="modal_background border-0 rounded-lg shadow-lg relative flex flex-col bg-white outline-none focus:outline-none"
            >
              {/* header */}
              <div className="w-full flex items-start justify-between border-b border-solid border-slate-200 rounded-t">
                <p className="page_title">{title}</p>
              </div>
              <div
                className="main_text w-auto"
                style={{
                  padding: "0px 20px 0px 20px",
                  overflowX: "auto",
                  overflowY: "auto",
                  minWidth: 0,
                }}
              >
                {getBody()}
              </div>
              <div className="flex items-center justify-end p-6 border-t border-solid border-slate-200 rounded-b">
                {getFooter()}
              </div>
            </div>
          </div>
        </div>
        <div
          className="opacity-25 fixed inset-0 z-40 bg-black"
          style={{ zIndex: 1400 }}
        />
      </>
    );
  };

  const showAsDrawer = () => {
    return (
      <>
        <Drawer
          ModalProps={{
            keepMounted: true,
            disableEscapeKeyDown: true,
          }}
          variant="temporary"
          anchor="right"
          open={showModal}
          onClose={(event, reason) => {
            if (reason !== "backdropClick") {
              setShowModal(false);
            }
          }}
          className="flex flex-col gap-2 p-2 modal_drawer"
          sx={{
            zIndex: 1402,
            "& .MuiDrawer-paper": {
              minWidth: "350px",
              width: "500px",
              maxWidth: "100%",
              height: "100%",
            },
            position: "fixed",
            top: 0,
            right: 0,
            overflow: "hidden",
          }}
        >
          <Box
            className="flex justify-between items-center p-4 menu_background_color"
            sx={{ borderBottom: "1px solid #ddd" }}
          >
            <Typography sx={{ color: "white" }}>{title}</Typography>
            {!hideCancelButton && (
              <IconButton
                sx={{ color: "white" }}
                onClick={() => {
                  onClickToCancel && onClickToCancel();
                  setShowModal(false);
                }}
              >
                <Close />
              </IconButton>
            )}
          </Box>

          <Box
            className="p-4 gap-2"
            sx={{
              display: "flex",
              flexDirection: "column-reverse",
              justifyContent: "space-between",
              height: "100vh",
              overflowY: "hidden",
            }}
          >
            {!hideFooterControls && (
              <Box
                className="w-full"
                sx={{
                  display: "flex",
                  flexDirection: "row-reverse",
                }}
              >
                <Box>{getFooter()}</Box>
              </Box>
            )}

            {hasRequiredValues && (
              <Alert className="w-full" severity="warning">
                <label className="text-xs">
                  {localize.mandatory_values_warning}
                </label>
              </Alert>
            )}

            <Box className="w-full" sx={{ flex: 1, overflowY: "auto" }}>
              {getBody()}
            </Box>
          </Box>
        </Drawer>
      </>
    );
  };

  const getBody = () => {
    return (
      <div
        style={
          isDrawerMode
            ? { height: customHeight }
            : {
                maxHeight: "500px",
                width: "100%",
                minWidth: 0,
              }
        }
      >
        {Component}
      </div>
    );
  };

  const getFooter = () => {
    return (
      <Box className="flex gap-2 items-center">
        {!hideCancelButton && (
          <Button
            size="small"
            color="error"
            variant="text"
            type="button"
            style={{ fontSize: "0.8em" }}
            onClick={onClickToCancel ?? (() => setShowModal(false))}
            className="app_general_button_data btn-error btn-outline"
          >
            {cancelButtonText ?? localize.cancel}
          </Button>
        )}

        {customSecondaryAction && (
          <Button
            sx={{ fontSize: "0.8em", color: "#10366E" }}
            size="small"
            variant="outlined"
            type="button"
            onClick={customSecondaryAction.action}
          >
            {customSecondaryAction.name}
          </Button>
        )}

        {!submitHidden && (
          <Button
            sx={{ fontSize: "0.8em", backgroundColor: "#10366E" }}
            size="small"
            variant="contained"
            type="button"
            disabled={validators || okButtonDisabled}
            onClick={() => {
              if (!okButtonAlwaysAvailable) setOkButtonDisabled(true);
              onClickToSubmit();
            }}
          >
            {okButtonText ?? localize.save_changes}
          </Button>
        )}
      </Box>
    );
  };

  return showModal && (!isDrawerMode ? showAsModal() : showAsDrawer());
}
